import { useEffect, useMemo, useRef, useState } from 'react';
import { LocalStorage } from 'utils/LocalStorage';

/**
 * The available options for the hook
 */
export type UseLocalStorageOptionsType<T = any, R = any> = {
  // If set to true, will automatically convert from string to JSON object
  isJSON: boolean;

  // If provided, this will allow changes to the value before returning from the hook
  transformer?: (value: T) => R;

  // The types of monitors to look for changes
  monitors: UseLocalStorageMonitorsType;

  // If set to false, this will stop monitoring after a valid (non-null) value was found
  // If set to true, this will continuously monitor and report changes. This requires
  //   at least one monitor to be set (storage or timed).
  continuous: boolean;
};

type UseLocalStorageMonitorsType = {
  // If set to true, this will monitor the 'storage' event on the window for changes
  storageEvent: boolean;

  // If set to true, this will use setTimeout to monitor for changes
  timed: boolean;

  // If provided and timed is true, this is the interval for how often we will look
  //   for changes (in milliseconds). The minimum allowed is 500ms. This is to protect
  //   the application from running too many intervals.
  timedIntervalMS: number;

  // If provided, this is the initial interval used until a non-null value is found.
  //   This property can be used for faster intervals for initial values. The minimum
  //   for this is 500ms, just like timedIntervalMS
  timedInitialIntervalMS: number;

  // If provided, the timedInitialIntervalMS will only attempt this many times before
  //   falling back to the timedIntervalMS. If this is set to <= 0, initial phase will
  //   be ignored completely.
  timedInitialAttempts: number;
};

/**
 * The default options for the hook. These are merged with the provided options
 */
const UseLocalStorageOptionsDefaults: UseLocalStorageOptionsType = {
  isJSON: false,
  monitors: {
    storageEvent: true,
    timed: false,
    timedIntervalMS: 5000,
    timedInitialIntervalMS: 500,
    timedInitialAttempts: 5,
  },
  continuous: true,
};

/**
 * Monitors local storage and returns the value for provided key
 *
 * @remarks
 * A hook that returns the value of the key provided within local storage.
 * Supports several options for configuring what type of monitoring and how
 * frequent changes are checked.
 *
 * @param key The key for which the data is stored within Local Storage
 * @param options Options for how to interact with the hook {@link UseLocalStorageOptionsDefaults}
 * @returns
 */
export function useLocalStorage<T = any, R = any>(
  key: string,
  options?: RecursivePartial<UseLocalStorageOptionsType<T, R>>,
): R {
  /*******************************
   * Set up and validate settings
   *******************************/
  const hookSettings = useMemo<UseLocalStorageOptionsType>(() => {
    // Merging defaults with provided options
    const settings = {
      ...UseLocalStorageOptionsDefaults,
      ...(options as UseLocalStorageOptionsType),
    };

    // Merge monitors if set
    if (options?.monitors) {
      settings.monitors = {
        ...UseLocalStorageOptionsDefaults.monitors,
        ...options.monitors,
      };

      settings.monitors.timedInitialIntervalMS =
        options.monitors.timedInitialIntervalMS ||
        options.monitors.timedIntervalMS ||
        UseLocalStorageOptionsDefaults.monitors.timedInitialIntervalMS;
    }

    /**************
     * Validations
     **************/

    // Time-monitor validations
    if (settings.monitors.timed) {
      // Verify that if timedIntervalMS is set, it is not below 500
      if (settings.monitors.timedIntervalMS < 500) {
        console.error('useLocalStorage timedIntervalMS cannot be lower than 500ms.');
        settings.monitors.timedIntervalMS = 500;
      }

      // Verify that if timedInitialAttempts is set, it is not below 0
      if (settings.monitors.timedInitialAttempts < 0) {
        console.error('useLocalStorage timedInitialAttempts cannot be below 0. Defaulting to 0');
        settings.monitors.timedInitialAttempts = 0;
      }
    }

    return settings;
  }, [options]);

  /*************
   * Hook logic
   *************/
  const initialAttempts = useRef<number>(0);
  const [initialTimedPhase, setInitialTimedPhase] = useState<boolean>(
    hookSettings.monitors.timedInitialAttempts > 0,
  );
  const [storageValue, setStorageValue] = useState(() => {
    const value = getStoredData();

    // Disable initial timed phase if value is truthy, timed monitor
    //   is enabled, and initialTimedPhase is enabled (> 0)
    if (value && initialTimedPhase && hookSettings.monitors.timed) {
      setInitialTimedPhase(false);
    }

    return value;
  });

  /**
   * Set up the monitors
   */
  useEffect(() => {
    // Exit early if continuous is disabled and value is found
    if (storageValue && !hookSettings.continuous) return;

    // Event monitoring
    let storageEventHandler: (event: StorageEvent) => void;
    if (hookSettings.monitors.storageEvent) {
      storageEventHandler = (event: StorageEvent) => {
        if (event.key === key) {
          const value = getStoredData();
          setStorageValue(value);
        }
      };
      window.addEventListener('storage', storageEventHandler);
    }

    // Timed monitoring
    let timedIntervalHandler: number;
    if (hookSettings.monitors.timed) {
      const timedEventInterval = initialTimedPhase
        ? hookSettings.monitors.timedInitialIntervalMS
        : hookSettings.monitors.timedIntervalMS;
      const timedEventHandler = () => {
        const value = getStoredData();

        if (!value) {
          initialAttempts.current++;
          if (initialAttempts.current >= hookSettings.monitors.timedInitialAttempts) {
            setInitialTimedPhase(false);
          }
          return;
        }

        setInitialTimedPhase(false);
        if (hookSettings.isJSON && JSON.stringify(value) !== JSON.stringify(storageValue)) {
          setStorageValue(value);
        } else if (!hookSettings.isJSON && value === storageValue) {
          setStorageValue(value);
        }
      };
      timedIntervalHandler = window.setInterval(timedEventHandler, timedEventInterval);
    }

    // Clear monitors when unmounted
    return () => {
      if (hookSettings.monitors.storageEvent) {
        window.removeEventListener('storage', storageEventHandler);
      }

      if (hookSettings.monitors.timed) {
        window.clearInterval(timedIntervalHandler);
      }
    };
  }, [storageValue, initialTimedPhase, hookSettings.continuous]);

  /**
   * Retrieve the data from local storage
   * @returns The value from local storage (or null if not exists)
   */
  function getStoredData() {
    let localStorageValue: any;
    if (hookSettings.isJSON) {
      localStorageValue = LocalStorage.tryGetJSONItem(key);
    } else {
      localStorageValue = LocalStorage.tryGetItem(key);
    }

    return localStorageValue;
  }

  /**
   * Use transformer if one is set
   */
  const transformedValue = useMemo(() => {
    if (hookSettings.transformer) {
      // Make a copy of the data, so the transformer isn't modifying
      //   the internal hook state
      let storedDataCopy = storageValue;
      if (storedDataCopy) {
        storedDataCopy = JSON.parse(JSON.stringify(storageValue));
      }

      return hookSettings.transformer(storedDataCopy);
    }

    return storageValue;
  }, [storageValue]);

  return transformedValue;
}
